import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useRouterState } from '@tanstack/react-router'
import { BookOpen, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { SkeletonRows } from '@/components/ui/misc'
import { useT } from '@/i18n'
import { toggleHelp, ui, useUI } from '@/lib/store'
import { cn } from '@/lib/utils'
import { HELP_DOCK_QUERY, HELP_PANEL_WIDTH, useMediaQuery } from './useMediaQuery'

export const HELP_BUTTON_ID = 'help-button'

/** `?help=<key>` on any page opens the panel at that entry (links from /implementation). */
function useHelpDeepLink() {
  const key = useRouterState({ select: (s) => (s.location.search as Record<string, unknown>).help })
  React.useEffect(() => {
    if (typeof key === 'string' && key) ui.set({ helpOpen: true, helpKey: key })
  }, [key])
}

/** The content (all entries, search) loads on first open, keeping it out of the main bundle. */
const HelpBody = React.lazy(() => import('./HelpBody'))

/**
 * Side help panel, available on every page (? / F1 / the «?» button in the top bar).
 * Docked and non-modal on wide screens (the page is pushed aside and stays usable);
 * a modal drawer with a focus trap on narrow screens. Esc closes it and focus returns
 * to the «?» button.
 */
export function HelpPanel() {
  useHelpDeepLink()
  const open = useUI((s) => s.helpOpen)
  const docked = useMediaQuery(HELP_DOCK_QUERY)
  const t = useT()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => toggleHelp(v)} modal={!docked}>
      <DialogPrimitive.Portal>
        {!docked && <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/25" />}
        <DialogPrimitive.Content
          data-help-panel=""
          data-testid="help-panel"
          aria-describedby={undefined}
          className={cn('fixed right-0 top-0 flex h-dvh flex-col border-l border-border bg-surface', docked ? 'z-40' : 'z-50 shadow-pop')}
          style={{ width: docked ? HELP_PANEL_WIDTH : 'min(420px, 100vw)' }}
          onInteractOutside={(e) => {
            // Docked: the page stays usable while help is open.
            if (docked) e.preventDefault()
          }}
          onCloseAutoFocus={(e) => {
            e.preventDefault()
            document.getElementById(HELP_BUTTON_ID)?.focus()
          }}
        >
          <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
            <div className="min-w-0">
              <DialogPrimitive.Title className="flex items-center gap-2 text-base font-semibold text-ink-strong">
                <BookOpen className="size-4 text-brand-strong dark:text-brand" aria-hidden /> {t('help.title')}
              </DialogPrimitive.Title>
              <p className="mt-0.5 text-[12px] text-muted">{t('help.keyHint')}</p>
            </div>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon-sm" aria-label={t('help.close')}>
                <X />
              </Button>
            </DialogPrimitive.Close>
          </div>
          {open && (
            <React.Suspense fallback={<SkeletonRows rows={6} className="p-4" />}>
              <HelpBody key={pathname} pathname={pathname} docked={docked} />
            </React.Suspense>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

